/**
 * Run mode, for one step: what the drawer shows instead of a form once the
 * flow is live — the concept's `RunView`, wired to `GET /stats` and
 * `GET /enrollments?node=`.
 *
 * Every number is the API's (`FlowNodeStats`); the people are the API's masked
 * people. Delivered / read / replied appear only when the API measured them —
 * an email has no "read" and a WhatsApp message no "open", and the contract
 * says so with null, which this draws as a sentence, never as 0.
 *
 * Opening a person shows their step log (`GET /enrollments/:id`) — what the
 * engine actually did, step by step, with its outcome and the reason a step
 * was skipped. That is the screen that answers "why did Asha not get the
 * reminder?".
 */
import {
  FLOW_ENROLLMENT_STATUS_LABEL,
  type FlowEnrollmentListResponse,
  FLOW_EXIT_REASON_LABEL,
  FLOW_NODE_TYPE_LABEL,
  type FlowEnrollmentDetailResponse,
  type FlowEnrollmentSummary,
  type FlowExitReason,
  type FlowNode,
  type FlowNodeStats,
} from '@wend/shared';
import { cn } from '@wend/ui';
import { useState } from 'react';

import { errorMessage } from '../../lib/api/errors';
import { useAsyncData } from '../../lib/use-async-data';
import { HINT, KICK } from './controls';
import { portLabel } from './forms';
import { fmtIst, n, type FlowEditorData } from './model';

/**
 * The two reads run mode makes, injected — the page wires `lib/api/flows`, and
 * a verify suite can render this without a network (or `import.meta.env`).
 */
export interface RunPeopleApi {
  enrollments: (
    flowId: string,
    filter: { page: number; pageSize: number; node?: string; version?: number },
    signal?: AbortSignal,
  ) => Promise<FlowEnrollmentListResponse>;
  enrollment: (flowId: string, enrollmentId: string, signal?: AbortSignal) => Promise<FlowEnrollmentDetailResponse>;
}

export function exitReasonText(reason: string | null): string | null {
  if (reason === null) return null;
  return (FLOW_EXIT_REASON_LABEL as Readonly<Record<string, string>>)[reason as FlowExitReason] ?? reason.replace(/_/g, ' ');
}

export function RunTiles({ node, stat }: { node: FlowNode; stat: FlowNodeStats | null }) {
  if (!stat) return <p className={HINT}>Nobody has reached this step yet.</p>;
  const terminal = node.type === 'goal' || node.type === 'exit';
  return (
    <div className="grid grid-cols-3 gap-2" aria-live="polite">
      {[
        { label: 'In step now', value: stat.waitingNow },
        { label: terminal ? 'Reached' : 'Passed', value: terminal ? stat.entered : stat.completed },
        { label: 'Converted', value: stat.converted, good: true },
      ].map((tile) => (
        <div key={tile.label} className="flex flex-col rounded-[10px] border border-line bg-white/[0.04] px-2.5 py-2">
          <span className="font-mono text-[9.5px] uppercase tracking-[0.08em] text-muted">{tile.label}</span>
          <b className={cn('text-[1.25rem] font-semibold tabular-nums text-white', tile.good && 'text-good')}>{n(tile.value)}</b>
        </div>
      ))}
    </div>
  );
}

export function RunBreakdown({ node, stat, data }: { node: FlowNode; stat: FlowNodeStats; data: FlowEditorData }) {
  const ports = Object.entries(stat.ports);
  const m = stat.messages;
  return (
    <ul className="m-0 list-none rounded-[10px] border border-line p-0 text-[13px]">
      <li className="flex justify-between gap-2.5 px-3 py-2"><span>Entered</span><b className="font-semibold tabular-nums text-white">{n(stat.entered)}</b></li>
      {ports.map(([port, count]) => (
        <li key={port} className="flex justify-between gap-2.5 border-t border-line px-3 py-2">
          <span>{portLabel(node, port, data)}</span>
          <b className="font-semibold tabular-nums text-white">{n(count)}</b>
        </li>
      ))}
      {m && (
        <>
          <li className="flex justify-between gap-2.5 border-t border-line px-3 py-2"><span>Sent</span><b className="font-semibold tabular-nums text-white">{n(m.sent)}</b></li>
          <li className="flex justify-between gap-2.5 border-t border-line px-3 py-2">
            <span>Delivered</span>
            {m.delivered === null ? <span className="text-muted">Not reported for this channel</span> : <b className="font-semibold tabular-nums text-white">{n(m.delivered)}</b>}
          </li>
          <li className="flex justify-between gap-2.5 border-t border-line px-3 py-2">
            <span>Read</span>
            {m.read === null ? <span className="text-muted">Not measured</span> : <b className="font-semibold tabular-nums text-white">{n(m.read)}</b>}
          </li>
          <li className="flex justify-between gap-2.5 border-t border-line px-3 py-2">
            <span>Replied</span>
            {m.replied === null ? <span className="text-muted">Not measured</span> : <b className="font-semibold tabular-nums text-white">{n(m.replied)}</b>}
          </li>
          <li className="flex justify-between gap-2.5 border-t border-line px-3 py-2"><span>Skipped</span><b className="font-semibold tabular-nums text-white">{n(m.skipped)}</b></li>
        </>
      )}
    </ul>
  );
}

function StepLog({ flowId, enrollment, onBack, api }: { flowId: string; enrollment: FlowEnrollmentSummary; onBack: () => void; api: RunPeopleApi }) {
  const detail = useAsyncData<FlowEnrollmentDetailResponse>((signal) => api.enrollment(flowId, enrollment.id, signal), [flowId, enrollment.id]);
  return (
    <div className="flex flex-col gap-2">
      <button type="button" onClick={onBack} className="self-start cursor-pointer border-0 bg-transparent p-0 text-[12.5px] text-brand-ink underline underline-offset-2 focus-visible:outline-2 focus-visible:outline-brand-b">
        ← Everyone at this step
      </button>
      <p className="m-0 text-[13.5px] font-semibold text-white">{enrollment.person.name ?? 'No name on file'}</p>
      <p className={HINT}>{enrollment.person.address ?? 'No address'} · {FLOW_ENROLLMENT_STATUS_LABEL[enrollment.status]}{enrollment.exitReason ? ` · ${exitReasonText(enrollment.exitReason)}` : ''}</p>
      {detail.status === 'error' && <p className={cn(HINT, 'text-bad')}>{errorMessage(detail.error)}</p>}
      {detail.loadingFirst && <p className={HINT}>Loading the step log…</p>}
      {detail.data && (
        <ol className="m-0 flex list-none flex-col gap-1 p-0">
          {detail.data.steps.map((step) => (
            <li key={step.id} className="rounded-lg border border-line px-2.5 py-1.5 text-[12.5px]">
              <b className="font-semibold text-white">{FLOW_NODE_TYPE_LABEL[step.kind]}</b> · {step.outcome.replace(/_/g, ' ')}
              {step.port && <span className="text-muted"> → {step.port}</span>}
              <small className="block font-mono text-[10.5px] text-muted">{fmtIst(step.at)}</small>
            </li>
          ))}
          {detail.data.steps.length === 0 && <li className={HINT}>No steps recorded yet.</li>}
        </ol>
      )}
    </div>
  );
}

/** People at one step of the running version, masked, newest first. */
export function RunPeople({ flowId, nodeId, version, api }: { flowId: string; nodeId: string; version: number; api: RunPeopleApi }) {
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState<FlowEnrollmentSummary | null>(null);
  const people = useAsyncData((signal) => api.enrollments(flowId, { page, pageSize: 20, node: nodeId, version }, signal), [flowId, nodeId, version, page]);

  if (open) return <StepLog flowId={flowId} enrollment={open} onBack={() => setOpen(null)} api={api} />;

  return (
    <section className="flex flex-col gap-2" aria-label="People at this step">
      <p className={KICK}>People at this step</p>
      {people.status === 'error' && <p className={cn(HINT, 'text-bad')}>{errorMessage(people.error)}</p>}
      {people.loadingFirst && <p className={HINT}>Loading…</p>}
      {people.data && people.data.items.length === 0 && <p className={HINT}>Nobody is at this step right now.</p>}
      {people.data && people.data.items.length > 0 && (
        <ul className="m-0 flex list-none flex-col gap-1 p-0">
          {people.data.items.map((item) => (
            <li key={item.id}>
              <button
                type="button"
                onClick={() => setOpen(item)}
                className="flex w-full cursor-pointer items-center justify-between gap-2 rounded-lg border border-line bg-white/[0.02] px-2.5 py-2 text-left text-[12.5px] hover:bg-white/[0.06] focus-visible:outline-2 focus-visible:outline-brand-b"
              >
                <span className="min-w-0">
                  <span className="block truncate text-white">{item.person.name ?? 'No name on file'}</span>
                  <span className="block truncate text-muted">{item.person.address ?? 'No address'}</span>
                </span>
                <span className="flex-none text-muted">{FLOW_ENROLLMENT_STATUS_LABEL[item.status]}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {people.data && people.data.pagination.totalPages > 1 && (
        <div className="flex items-center justify-between text-[12px] text-muted">
          <button type="button" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="cursor-pointer disabled:opacity-40">Previous</button>
          <span>Page {page} of {n(people.data.pagination.totalPages)}</span>
          <button type="button" disabled={page >= people.data.pagination.totalPages} onClick={() => setPage((p) => p + 1)} className="cursor-pointer disabled:opacity-40">Next</button>
        </div>
      )}
    </section>
  );
}
